// Phygital · visual Component Library.
// Every reusable primitive from `./primitives.jsx` is rendered here for
// consistency + hand-off. Also lists design tokens + color palette.

import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Check, ChevronRight, Clock, MapPin, Plus, Save, User,
} from "lucide-react";
import {
  TOKENS, CATEGORY_COLORS, Tag, Btn, SegButton, SegRow, Pill, TextInput, TextArea,
  FieldLabel, SectionLabel, HintText, Header, Collapsible, Swatch, VeinHalf, OptionTile,
  HostRow, LockBadge, InlineNote, DisclosureRow, CloseButton, VisibilitySelect,
} from "./primitives";
import AdminBar from "./AdminBar";

const Section = ({ id, title, sub, children }) => (
  <section id={id} className="border-b border-stone-100 py-6 px-4">
    <div className="flex items-baseline justify-between mb-4">
      <div>
        <h2 className="text-lg font-bold text-stone-900" data-testid={`section-${id}`}>{title}</h2>
        {sub && <p className="text-[11px] text-stone-500 mt-0.5">{sub}</p>}
      </div>
    </div>
    <div className="space-y-4">{children}</div>
  </section>
);

const Row = ({ label, children }) => (
  <div>
    <p className="text-[9px] uppercase tracking-wider text-stone-400 font-semibold mb-2">{label}</p>
    <div className="flex flex-wrap items-center gap-2">{children}</div>
  </div>
);

export default function PhygitalComponentLibrary() {
  const [pill, setPill] = useState("Combo");
  const [text, setText] = useState("");
  const [notes, setNotes] = useState("");
  const [swatch, setSwatch] = useState("#8b5cf6");
  const [vein, setVein] = useState("warm");
  const [seg, setSeg] = useState("tbd");
  const [tile, setTile] = useState("almond");
  const [vis, setVis] = useState("host");

  const goto = (id) => () => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className={TOKENS.PAGE} data-testid="phygital-component-library">
      <Header
        title="Phygital Beauty · Components"
        sub="v1 · shared primitives"
        back="/"
        right={
          <Link to="/me" className="text-[10px] font-bold text-purple-700 uppercase tracking-wider" data-testid="goto-live">Dashboard →</Link>
        }
      />

      <div className={TOKENS.FRAME}>
        <div className="px-4 pt-4 pb-2">
          <h1 className="text-2xl font-bold text-stone-900 tracking-tight">Component Library</h1>
          <p className="text-sm text-stone-500 mt-1">Every reusable primitive. Import from <code className="bg-stone-100 px-1 rounded text-[11px]">./primitives.jsx</code>.</p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {["tokens", "colors", "typography", "tags", "buttons", "segmented", "pills", "inputs", "swatches", "tiles", "hosts", "labels", "collapsibles", "visibility", "notes", "misc"].map(id => (
              <button key={id} onClick={goto(id)} data-testid={`toc-${id}`}
                className="text-[10px] uppercase tracking-wider bg-stone-100 hover:bg-stone-200 rounded-full px-2.5 py-1 font-semibold text-stone-700">
                {id}
              </button>
            ))}
          </div>
        </div>

        <Section id="tokens" title="Design tokens" sub="Single source of truth">
          <pre className="bg-stone-50 border border-stone-200 rounded-xl p-3 text-[11px] text-stone-800 overflow-x-auto">{`FRAME  = "${TOKENS.FRAME}"
INK    = "${TOKENS.INK}"
ACCENT = "${TOKENS.ACCENT}"
ACCENT_SOFT = "${TOKENS.ACCENT_SOFT}"
ROSE   = "${TOKENS.ROSE}"
MAX_W  = ${TOKENS.MAX_W}px`}</pre>
        </Section>

        <Section id="colors" title="Category colors" sub="One accent per category">
          <div className="grid grid-cols-3 gap-2">
            {Object.entries(CATEGORY_COLORS).map(([id, c]) => (
              <div key={id} className="rounded-xl border border-stone-200 p-2" data-testid={`color-${id}`}>
                <div className="h-14 rounded-lg mb-1.5" style={{ backgroundColor: c.hex }} />
                <div className="h-4 rounded-md mb-1" style={{ backgroundColor: c.soft }} />
                <p className="text-[11px] font-bold capitalize">{id}</p>
                <p className="text-[9px] text-stone-500">{c.hex} / {c.soft}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section id="typography" title="Typography">
          <div className="space-y-2">
            <p className="text-2xl font-bold text-stone-900">Heading 1 · 24 bold</p>
            <p className="text-lg font-bold text-stone-900">Heading 2 · 18 bold</p>
            <p className="text-sm font-semibold text-stone-900">Body semibold · 14</p>
            <p className="text-sm text-stone-700">Body regular · 14</p>
            <p className="text-xs text-stone-500">Caption · 12 stone-500</p>
            <SectionLabel>Section label · 10 uppercase tracking</SectionLabel>
            <HintText>Hint text · 10 stone-500.</HintText>
          </div>
        </Section>

        <Section id="tags" title="Tags" sub='(never "chip" - vocabulary lock)'>
          <Row label="Tones">
            <Tag>Stone</Tag>
            <Tag tone="accent">Accent</Tag>
            <Tag tone="amber">Amber</Tag>
            <Tag tone="emerald">Emerald</Tag>
            <Tag tone="rose">Rose</Tag>
            <Tag tone="ink">Ink</Tag>
            <Tag tone="outline">Outline</Tag>
          </Row>
        </Section>

        <Section id="buttons" title="Buttons">
          <Row label="Tones">
            <Btn testid="btn-ink" tone="ink">Ink</Btn>
            <Btn testid="btn-outline" tone="outline">Outline</Btn>
            <Btn testid="btn-accent" tone="accent">Accent</Btn>
            <Btn testid="btn-ghost" tone="ghost">Ghost</Btn>
            <Btn testid="btn-danger" tone="danger">Danger</Btn>
          </Row>
          <Row label="Sizes">
            <Btn size="sm" icon={Save}>Small</Btn>
            <Btn size="md" icon={Check}>Medium</Btn>
            <Btn size="lg" icon={Plus} tone="accent">Large</Btn>
          </Row>
        </Section>

        <Section id="segmented" title="Segmented row" sub="Who / Where / When 3-way choices">
          <SegRow>
            <SegButton Icon={User} selected={seg === "diy"} onClick={() => setSeg("diy")} testid="seg-diy">DIY</SegButton>
            <SegButton Icon={Clock} selected={seg === "tbd"} onClick={() => setSeg("tbd")} testid="seg-tbd">TBD</SegButton>
            <SegButton Icon={Plus} selected={seg === "add"} onClick={() => setSeg("add")} testid="seg-add">Add</SegButton>
          </SegRow>
        </Section>

        <Section id="pills" title="Pills" sub="Preference options">
          <Row label="Cream (default)">
            {["Dry", "Oily", "Combo", "Sensitive"].map(l => (
              <Pill key={l} selected={pill === l} onClick={() => setPill(l)} testid={`pill-${l}`}>{l}</Pill>
            ))}
          </Row>
          <Row label="Accent">
            <Pill tone="accent" selected>Matte</Pill>
            <Pill tone="accent">Dewy</Pill>
          </Row>
          <Row label="Rose (universal defaults)">
            <Pill tone="rose" selected>Latex</Pill>
            <Pill tone="rose">Fragrance</Pill>
          </Row>
        </Section>

        <Section id="inputs" title="Inputs">
          <FieldLabel>City, State, Country</FieldLabel>
          <TextInput value={text} onChange={setText} placeholder="Los Angeles, CA" icon={MapPin} testid="demo-text" />
          <HintText>Just the city, not a full address.</HintText>
          <FieldLabel>Anything else your host should know</FieldLabel>
          <TextArea value={notes} onChange={setNotes} placeholder="e.g. sensitive to strong scents" testid="demo-textarea" />
        </Section>

        <Section id="swatches" title="Swatches & veins">
          <Row label="Foundation swatches">
            {["#f5e0c8", "#e5c99a", "#c99d70", "#8f6541", "#5a3d24"].map(hex => (
              <Swatch key={hex} hex={hex} selected={swatch === hex} onClick={() => setSwatch(hex)} testid={`swatch-${hex}`} label={hex} />
            ))}
          </Row>
          <Row label="Undertone (vein-half)">
            <VeinHalf top="#7c3aed" bottom="#3b82f6" selected={vein === "cool"} onClick={() => setVein("cool")} testid="vein-cool" label="Cool" />
            <VeinHalf top="#3b82f6" bottom="#16a34a" selected={vein === "neutral"} onClick={() => setVein("neutral")} testid="vein-neutral" label="Neutral" />
            <VeinHalf top="#65a30d" bottom="#a3a11c" selected={vein === "warm"} onClick={() => setVein("warm")} testid="vein-warm" label="Warm" />
          </Row>
        </Section>

        <Section id="tiles" title="Option tiles" sub="Eye shape / Lash style">
          <div className="grid grid-cols-3 gap-2">
            {["almond", "round", "hooded"].map(k => (
              <OptionTile key={k} label={k[0].toUpperCase() + k.slice(1)} selected={tile === k} onClick={() => setTile(k)}
                testid={`tile-${k}`} visual={<div className="w-8 h-4 border-2 border-current rounded-full" />} />
            ))}
          </div>
        </Section>

        <Section id="hosts" title="Host row" sub="Rolodex + directory picker">
          <HostRow name="Shelsea Patton" brand="Privé Beauté" selected testid="host-1" right={<Check className="w-4 h-4" />} />
          <HostRow name="Mia Thomas" brand="Mia Makeup Studio" testid="host-2" right={<ChevronRight className="w-4 h-4 text-stone-400" />} />
          <HostRow name="Aria James" testid="host-3" />
        </Section>

        <Section id="labels" title="Field labels & lock badges">
          <FieldLabel right={<LockBadge>Private</LockBadge>}>Allergies</FieldLabel>
          <div className="flex flex-wrap gap-1.5">
            <Pill tone="rose" selected>Latex</Pill>
            <Pill tone="rose">Fragrance</Pill>
          </div>
        </Section>

        <Section id="collapsibles" title="Collapsible sections" sub="Universal defaults default to closed">
          <Collapsible label="Allergies" summary="None" testid="collapse-allergies">
            <p className="text-[11px] text-stone-500">Selected tags render inside.</p>
          </Collapsible>
          <Collapsible label="Accessibility" summary="Low fragrance" defaultOpen testid="collapse-access">
            <p className="text-[11px] text-stone-500">Open-by-default variant.</p>
          </Collapsible>
        </Section>

        <Section id="visibility" title="Visibility selector" sub="3-way per-field control (Only me / My hosts / Public link)">
          <VisibilitySelect testid="demo-vis" value={vis} onChange={setVis} />
          <p className="text-[11px] text-stone-500 mt-2">Current: <b>{vis}</b></p>
        </Section>

        <Section id="notes" title="Inline notes">
          <InlineNote tone="info">Hosts see your universal defaults only when you allow it.</InlineNote>
          <InlineNote tone="success">Preferences saved.</InlineNote>
          <InlineNote tone="warn">You haven&apos;t picked a service yet.</InlineNote>
        </Section>

        <Section id="misc" title="Miscellaneous">
          <Row label="Close button">
            <CloseButton onClick={() => alert("close")} testid="demo-close" />
          </Row>
          <DisclosureRow label="Occasion" value="Wedding · Trial" onClick={() => {}} testid="demo-disclosure" />
          <DisclosureRow label="Where" value="Just the city, TBD" muted onClick={() => {}} testid="demo-disclosure-muted" />
        </Section>

        <div className="text-center py-8">
          <p className="text-[10px] text-stone-400 uppercase tracking-wider">Phygital Beauty · Component Library · v1</p>
          <Link to="/me" className="text-xs text-purple-700 font-semibold underline mt-1 inline-block" data-testid="library-goto-flow">Back to my Dashboard →</Link>
        </div>
      </div>
      <AdminBar />
    </div>
  );
}
